/**
 * SCD Lot 3 — Marketing, satisfaction, promotions, périodes commerciales,
 * segments clients et effets d'événements retail. Helpers purs consommés
 * par retailSlice (tick), eventSlice (résolution d'événements) et les
 * écrans Marketing / Dashboard.
 */
import segmentsData from '../data/dgs/segments_clients.json';
import marketingData from '../data/dgs/marketing.json';
import { getSecteur, prixConseille } from './retail';

export const LEVIERS = marketingData.leviers;
export const getLevier = (id) => LEVIERS.find(l => l.id === id) || null;
export const PERIODES = marketingData.periodes;
export const PROMO_CONFIG = marketingData.promo;
export const SEGMENTS = segmentsData.segments;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/** État marketing initial d'un magasin (aussi utilisé en migration de save). */
export const defaultMarketing = () => ({
  leviers: {},
  installes: {},
  promo: null,
  habituation: 0,
  satisfaction: 60,
  noteAvis: 3.0,
  nbAvis: 12,
  effetsTemporaires: [],
});

/** Période commerciale active pour un couple (mois, jour) — calendrier 30 j. */
export const getPeriodeActive = (month, day) => {
  const cur = month * 100 + day;
  return PERIODES.find(p => {
    const start = p.mois_debut * 100 + p.jour_debut;
    const end = p.mois_fin * 100 + p.jour_fin;
    return start <= end ? (cur >= start && cur <= end) : (cur >= start || cur <= end);
  }) || null;
};

/** Prochaine période commerciale (pour le calendrier affiché au joueur). */
export const getProchainesPeriodes = (month, day) => {
  const cur = month * 100 + day;
  return [...PERIODES]
    .map(p => ({ ...p, _start: p.mois_debut * 100 + p.jour_debut }))
    .sort((a, b) => {
      const da = a._start >= cur ? a._start - cur : a._start + 1200 - cur;
      const db = b._start >= cur ? b._start - cur : b._start + 1200 - cur;
      return da - db;
    });
};

/** Indice prix moyen du magasin = moyenne (prix pratiqué / prix conseillé) sur les familles actives. */
export const indexPrixMoyen = (retail) => {
  const sec = getSecteur(retail.secteur);
  if (!sec) return 1;
  let sum = 0; let n = 0;
  sec.familles.forEach(f => {
    const a = retail.assortiment?.[f.id];
    if (!a || !a.actif) return;
    const conseille = prixConseille(f, retail.positionnement);
    if (conseille > 0) { sum += a.prixVente / conseille; n += 1; }
  });
  return n > 0 ? Math.round((sum / n) * 100) / 100 : 1;
};

/** Part de l'assortiment actif (0-1). */
export const partAssortimentActif = (retail) => {
  const sec = getSecteur(retail.secteur);
  if (!sec || sec.familles.length === 0) return 1;
  const actifs = sec.familles.filter(f => retail.assortiment?.[f.id]?.actif).length;
  return actifs / sec.familles.length;
};

/**
 * Attractivité par segment : réaction de chaque segment au prix moyen,
 * au niveau de service (satisfaction) et à la largeur de choix.
 * Retourne { mult, detail: [{ segment, prixResp, serviceResp, choixResp, mult }] }.
 */
export const computeSegmentsDetail = (retail) => {
  const mkt = retail.marketing || defaultMarketing();
  const idx = indexPrixMoyen(retail);
  const sat = mkt.satisfaction ?? 60;
  const part = partAssortimentActif(retail);
  const detail = SEGMENTS.map(seg => {
    const prixResp = clamp(1 - seg.sensibilite_prix * (idx - 1), 0.5, 1.4);
    const serviceResp = clamp(1 + seg.sensibilite_service * ((sat - 60) / 100), 0.7, 1.3);
    const choixResp = clamp(1 + seg.sensibilite_choix * (part - 1), 0.7, 1.05);
    const mult = Math.round(prixResp * serviceResp * choixResp * 100) / 100;
    return { segment: seg, prixResp, serviceResp, choixResp, mult };
  });
  const mult = detail.reduce((a, d) => a + (d.segment.poids_pct / 100) * d.mult, 0);
  return { mult: Math.round(mult * 100) / 100, detail };
};

/**
 * Agrège tous les effets marketing du jour. Retourne les multiplicateurs de
 * trafic (leviers, avis, promo, période, événements, segments), les bonus de
 * conversion/panier, le coût quotidien et les métadonnées pour l'UI.
 */
export const computeMarketingEffects = (retail, { month, day, totalDays }) => {
  const mkt = retail.marketing || defaultMarketing();

  let traficLeviers = 1;
  let conversionBonus = 0;
  let panierBonus = 0;
  let satisfactionJour = 0;
  let margeMalusPctCa = 0;
  let coutJour = 0;
  LEVIERS.forEach(l => {
    if (!mkt.leviers?.[l.id]) return;
    traficLeviers *= 1 + (l.trafic_bonus || 0);
    conversionBonus += l.conversion_bonus || 0;
    panierBonus += l.panier_bonus || 0;
    satisfactionJour += l.satisfaction_jour || 0;
    margeMalusPctCa += l.marge_malus_pct_ca || 0;
    coutJour += (l.cout_mensuel || 0) / 30;
  });
  traficLeviers = Math.round(traficLeviers * 1000) / 1000;

  const noteAvis = mkt.noteAvis ?? 3;
  const avisMult = Math.round((0.85 + ((noteAvis - 1) / 4) * 0.30) * 100) / 100;

  const promoActive = !!(mkt.promo && mkt.promo.until > totalDays);
  const promoPct = promoActive ? mkt.promo.pct : 0;
  const promoTraficMult = promoActive
    ? Math.round((1 + (promoPct / 100) * PROMO_CONFIG.boost_trafic_par_pct * (1 - (mkt.habituation || 0))) * 100) / 100
    : 1;

  const periode = getPeriodeActive(month, day);
  let periodeMult = periode ? periode.trafic_mult : 1;
  if (periode?.attente_promo && !promoActive) periodeMult = Math.round(periodeMult * PROMO_CONFIG.malus_attente_promo * 100) / 100;

  const evts = (mkt.effetsTemporaires || []).filter(e => e.until > totalDays);
  let evtTraficMult = 1;
  let evtConversionMult = 1;
  let evtSurcoutPctCa = 0;
  evts.forEach(e => {
    evtTraficMult *= e.traficMult || 1;
    evtConversionMult *= e.conversionMult || 1;
    evtSurcoutPctCa += e.surcoutPctCa || 0;
  });
  evtTraficMult = Math.round(evtTraficMult * 100) / 100;
  evtConversionMult = Math.round(evtConversionMult * 100) / 100;

  const seg = computeSegmentsDetail({ ...retail, marketing: mkt });

  return {
    traficLeviers, conversionBonus, panierBonus, satisfactionJour, margeMalusPctCa, coutJour,
    avisMult, periode, periodeMult,
    promoActive, promoPct, promoTraficMult,
    evts, evtTraficMult, evtConversionMult, evtSurcoutPctCa,
    segmentMult: seg.mult, segmentsDetail: seg.detail,
    traficTotalMult: Math.round(traficLeviers * avisMult * periodeMult * promoTraficMult * evtTraficMult * seg.mult * 1000) / 1000,
  };
};

/**
 * Applique l'effet retail d'un choix d'événement (appelé par eventSlice).
 * fx : { traficMult, conversionMult, surcoutPctCa, duree_jours,
 *        satisfactionDelta, avisDelta, stockPertePct, stockBonusPct, label }
 * Retourne le nouvel objet retail (immutable).
 */
export const applyRetailEventEffect = (retail, fx, totalDays, eventName = 'Événement') => {
  const mkt = { ...(retail.marketing || defaultMarketing()) };
  if (fx.traficMult || fx.conversionMult || fx.surcoutPctCa) {
    mkt.effetsTemporaires = [
      ...(mkt.effetsTemporaires || []),
      {
        label: fx.label || eventName,
        traficMult: fx.traficMult || 1,
        conversionMult: fx.conversionMult || 1,
        surcoutPctCa: fx.surcoutPctCa || 0,
        until: totalDays + (fx.duree_jours || 7),
      },
    ];
  }
  if (fx.satisfactionDelta) mkt.satisfaction = clamp((mkt.satisfaction ?? 60) + fx.satisfactionDelta, 0, 100);
  if (fx.avisDelta) mkt.noteAvis = clamp((mkt.noteAvis ?? 3) + fx.avisDelta, 1, 5);

  let stock = retail.stock;
  let stats = retail.stats;
  if (fx.stockPertePct) {
    stock = {};
    let pertesEuros = 0;
    let pertesUnites = 0;
    Object.entries(retail.stock || {}).forEach(([k, st]) => {
      const perte = Math.ceil(st.qty * (fx.stockPertePct / 100));
      stock[k] = { ...st, qty: Math.max(0, st.qty - perte) };
      pertesEuros += perte * st.cump;
      pertesUnites += perte;
    });
    stats = {
      ...retail.stats,
      demarqueEurosCumul: Math.round((retail.stats?.demarqueEurosCumul || 0) + pertesEuros),
      demarqueUnitesCumul: (retail.stats?.demarqueUnitesCumul || 0) + pertesUnites,
    };
  } else if (fx.stockBonusPct) {
    stock = {};
    Object.entries(retail.stock || {}).forEach(([k, st]) => {
      stock[k] = { ...st, qty: st.qty + Math.round(st.qty * (fx.stockBonusPct / 100)) };
    });
  }
  return { ...retail, marketing: mkt, stock, stats };
};
